import React, { useCallback, useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { useBlocker } from 'react-router-dom';
import { BaseButton, Spinner } from '../base';
import { mdiDownload, mdiOpenInNew } from '@mdi/js';
import Icon from '@mdi/react';
import { getFileTypeInfo, isPdfFile, isImageFile, FileTypeInfo } from '../../utils/file-type-utils';
import * as S from './document-viewer.styles';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

export interface DocumentViewerProps {
    url: string;
    documentName?: string;
    metadata?: string;
    enableEditing?: boolean;
    loading?: boolean;
    overlays?: any[];
    onOverlayClick?: (key: string) => void;
    onMetadataUpdated?: (metadata: string) => Promise<void>;
    onDocumentUpdated?: (buffer: ArrayBuffer) => Promise<void>;
    onParseDocument?: () => Promise<void>;
}

export type DocumentMetadata = any;
export type OverlayMetadata = {
    height: number;
    left: number;
    top: number;
    width: number;
    pageIndex: number;
    key: string;
};

const DocumentViewerWithBlocker: React.FC<DocumentViewerProps> = (props) => {
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) => 
            !!props.loading && currentLocation.pathname !== nextLocation.pathname
    );

    useEffect(() => {
        if (blocker && blocker.state === "blocked") {
            if (window.confirm("The document is still loading. If you leave this page, your changes may not be saved. Do you still want to leave the page?")) {
                blocker.proceed();
            } else {
                blocker.reset();
            }
        }
    }, [blocker]);

    return <DocumentViewer {...props} />;
};

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
    url,
    documentName,
    metadata,
    enableEditing,
    loading,
    overlays = [],
    onOverlayClick,
    onMetadataUpdated,
    onDocumentUpdated,
    onParseDocument,
}) => {
    const fileInfo: FileTypeInfo = getFileTypeInfo(url, documentName);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;

        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.clientWidth);
            }
        };

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    setContainerWidth(entry.contentRect.width);
                }
            });
            resizeObserver.observe(containerRef.current);
        }

        window.addEventListener('resize', updateWidth);

        return () => {
            window.removeEventListener('resize', updateWidth);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, []);

    const handleOpenInNewTab = () => {
        window.open(url, '_blank');
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = url;
        link.download = documentName || 'document';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderPdfViewer = () => {
        return (
            <S.PdfContainer ref={containerRef}>
                <Document
                    file={url}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<Spinner />}
                >
                    {numPages && Array.from({ length: numPages }, (_, index) => (
                        <Page
                            key={`page_${index + 1}`}
                            pageNumber={index + 1}
                            width={containerWidth}
                        />
                    ))}
                </Document>
            </S.PdfContainer>
        );
    };

    const renderImageViewer = () => {
        return (
            <S.ImageContainer>
                <img 
                    src={url} 
                    alt={documentName || 'Document'} 
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
            </S.ImageContainer>
        );
    };

    
    const renderContent = () => {
        if (isPdfFile(fileInfo)) {
            return renderPdfViewer();
        } else if (isImageFile(fileInfo)) {
            return renderImageViewer();
        } else {
            return renderPdfViewer();
        }
    };

    const shouldShowDownloadButtons = !isPdfFile(fileInfo) && !isImageFile(fileInfo);

    return (
        <S.Container>
            {shouldShowDownloadButtons && (
                <div style={{ 
                    position: 'fixed', 
                    bottom: '20px', 
                    left: '50%', 
                    transform: 'translateX(-50%)', 
                    zIndex: 9999, 
                    display: 'flex', 
                    gap: '12px', 
                    background: 'white', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)' 
                }}>
                    <BaseButton
                        variant="primary"
                        onClick={handleDownload}
                    >
                        <Icon path={mdiDownload} size={0.75} />
                        Download
                    </BaseButton>
                    <BaseButton
                        variant="outlined"
                        onClick={handleOpenInNewTab}
                    >
                        <Icon path={mdiOpenInNew} size={0.75} />
                        Open in New Tab
                    </BaseButton>
                </div>
            )}
            {loading && (
                <S.LoadingContainer>
                    <Spinner />
                </S.LoadingContainer>
            )}
            {renderContent()}
        </S.Container>
    );
};

export { DocumentViewerWithBlocker };
export default DocumentViewer;
